import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useCreateSessionMutation,
  useLazyCheckAvailabilityQuery,
  useUpdateSessionMutation,
} from '../../../../app/store/services/manager.service'
import { sessionDurations } from '../../../../constants/sessionDurations'
import { SuggestedDate } from '../../../../interfaces/main-interfaces'
import { AppDatePicker } from '../../../../lib/shared/DatePicker'
import { FormField } from '../../../../lib/shared/FormField'
import { FormSelect } from '../../../../lib/shared/FormSelect'
import { CheckSlotsDTO, CreateSessionDTO } from '../dto/scheduling.dto'
import { SuggestionItem } from './SuggestionItem'
import {
  CreateSessionContent,
  CreateSessionForm,
  CreateSessionWrapper,
  SuggesstionList,
} from './create-session.style'

const convertToYYYYMMDD = (date: string) => {
  return moment(date).format('YYYY-MM-DD')
}

export function CreateSession({
  initialData,
  onClose,
  open,
  defaultTitle,
  canBookSession,
  defaultIndividualTitle,
}: {
  open: boolean
  onClose: () => void
  initialData: CreateSessionDTO | null
  defaultTitle: string
  canBookSession: { team: boolean; individual: boolean }
  defaultIndividualTitle: string
}) {
  const { t } = useTranslation()

  const [
    triggerCheckAvailability,
    { data: availabilitySlots, error: availabilitySlotsError },
  ] = useLazyCheckAvailabilityQuery()
  const [selectedSlot, setSelectedSlot] = useState('')
  const [suggestions, setSuggestions] = useState<SuggestedDate[]>([])
  const [loading, setLoading] = useState(false)

  const [createSession, { isLoading }] = useCreateSessionMutation()
  const [updateSession, { isLoading: updateLoading }] =
    useUpdateSessionMutation()

  const isIndividualCoaching = initialData?.type === 'individual'

  const messages = {
    success: initialData?.id ? 'changesSaved' : 'sessionCreated',
    error: initialData?.id ? 'sessionCreateError' : 'sessionUpdateError',
  }

  const { control, reset, handleSubmit, setValue, watch } = useForm({
    defaultValues: initialData || {},
  })
  useEffect(() => {
    if (!availabilitySlots) return
    setSuggestions([
      ...availabilitySlots.data.free,
      ...availabilitySlots.data.partial,
    ])
    setLoading(false)
  }, [availabilitySlots])

  const confirm = useConfirm()

  const handleMutate = useCallback(
    async (body: any) => {
      const response = initialData?.id
        ? await updateSession({
            body,
            id: initialData.id,
          }).unwrap()
        : await createSession(body).unwrap()
      // TODO: Handle if there's an error in response message
      console.log(response)

      toast.success(t(messages.success))

      onClose()
    },
    [createSession, initialData, messages.success, onClose, t, updateSession],
  )

  const onSubmit = handleSubmit(async (data) => {
    const start = moment(data.start).toISOString()
    const today = moment()
    // Check whether start time is in the past.
    const startTimeIsInPast = moment(start).isBefore(today)
    if (startTimeIsInPast) {
      toast.error(t('startingDateIsPast'))
      return
    }
    try {
      const body = {
        duration: data.duration,
        start,
        title: data.title,
        type: isIndividualCoaching ? 'individual' : 'team',
      }
      handleMutate(body)
      // confirm({
      //   title: t('warning'),
      //   description: t('coachIsNotAvailable'),
      //   confirmationText: t('anywayProceed'),
      // }).then(() => {
      //   handleMutate(body)
      // })
    } catch (error: any) {
      console.log(error)
      toast.error(t(messages.error))
    }
  })

  const handleCheck = useCallback(
    async (data: CheckSlotsDTO) => {
      try {
        setLoading(true)
        await triggerCheckAvailability({
          date: data.date,
          duration: data.duration,
          type: data.type,
        }).unwrap()
      } catch (error: any) {
        console.log('check slots error:', error)
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    },
    [triggerCheckAvailability],
  )

  useEffect(() => {
    reset(
      initialData
        ? {
            duration: initialData.duration,
            start: initialData.start,
            title: initialData.title,
            type: initialData.type,
          }
        : {},
    )
    if (initialData) {
      handleCheck({
        date: convertToYYYYMMDD(initialData.start),
        duration: initialData.duration,
        type: initialData.type,
      })
    }
  }, [handleCheck, initialData, reset])

  const suggestionsPartial = suggestions.filter(
    (item) => item?.availableMembers,
  )

  if (
    (isIndividualCoaching && !canBookSession.individual) ||
    (!isIndividualCoaching && !canBookSession.team)
  ) {
    return (
      <CreateSessionWrapper open={open} onClose={onClose}>
        <DialogTitle>
          {isIndividualCoaching
            ? t(
                initialData?.id
                  ? 'updateIndividualSession'
                  : 'bookIndividualSession',
              )
            : t(initialData?.id ? 'updateTeamSession' : 'bookTeamSession')}
        </DialogTitle>
        <DialogContent>
          <p>
            {isIndividualCoaching
              ? t('no_individual_coaching_sessions_left')
              : t('no_team_coaching_sessions_left')}
          </p>
          <p
            dangerouslySetInnerHTML={{
              __html: t('contact_hupo_for_more_sessions').replace(
                'team@hupo.co',
                '<a style="color: blue; text-decoration: underline" href="mailto:team@hupo.co">team@hupo.co</a>',
              ),
            }}
          ></p>
        </DialogContent>
        <DialogActions
          sx={{ display: 'grid', justifyContent: 'stretch', p: '16px 22px' }}
        >
          <Button
            type="submit"
            variant="contained"
            size="small"
            onClick={onClose}
          >
            {t('close')}
          </Button>
        </DialogActions>
      </CreateSessionWrapper>
    )
  }

  return (
    <CreateSessionWrapper open={open} onClose={onClose}>
      <DialogTitle>
        {isIndividualCoaching
          ? t(
              initialData?.id
                ? 'updateIndividualSession'
                : 'bookIndividualSession',
            )
          : t(initialData?.id ? 'updateTeamSession' : 'bookTeamSession')}
      </DialogTitle>
      <DialogContent>
        <CreateSessionForm id="sessionForm" onSubmit={onSubmit}>
          <CreateSessionContent>
            <FormField
              variant="filled"
              label={t('title')}
              control={control}
              defaultValue={
                isIndividualCoaching ? defaultIndividualTitle : defaultTitle
              }
              name="title"
              // rules={{ required: { value: true, message: t("requiredField") } }}
            />
            <AppDatePicker
              variant="filled"
              label={t('startTime')}
              control={control}
              name="start"
              onChange={() => {
                setSelectedSlot('')
                handleCheck({
                  date: convertToYYYYMMDD(watch('start')),
                  duration: watch('duration'),
                  type: initialData?.type ?? 'team',
                })
              }}
              rules={{ required: { value: true, message: t('requiredField') } }}
            />
            <FormSelect
              variant="filled"
              label={t('duration')}
              control={control}
              name="duration"
              rules={{ required: { value: true, message: t('requiredField') } }}
              options={sessionDurations.filter(
                (item) => item.value === initialData?.duration,
              )}
              onChange={() => {
                setSelectedSlot('')
                handleCheck({
                  date: convertToYYYYMMDD(watch('start')),
                  duration: watch('duration'),
                  type: initialData?.type ?? 'team',
                })
              }}
            />
            {
              <SuggesstionList>
                <h4> {t('suggestions')} </h4>
                <div className={`list ${loading ? 'loading' : ''}`}>
                  {loading ? (
                    <CircularProgress />
                  ) : suggestions.length > 0 ? (
                    <>
                      <Typography fontWeight="600" fontSize="14px">
                        {t('fullyAvailable')}
                      </Typography>
                      {suggestions
                        .filter((item) => !item?.availableMembers)
                        .map((slot, index) => (
                          <SuggestionItem
                            selectedSlot={selectedSlot}
                            setSelectedSlot={(value: string) => {
                              setSelectedSlot(value)
                              setValue('start', moment(value).toISOString())
                            }}
                            slot={slot}
                            key={`suggestion_${index}`}
                          />
                        ))}
                      {suggestionsPartial.length > 0 && (
                        <Stack pt="12px">
                          <Typography fontWeight="600" fontSize="14px">
                            {t('partiallyAvailable')}
                          </Typography>
                        </Stack>
                      )}
                      {suggestionsPartial.map((slot, index) => (
                        <SuggestionItem
                          selectedSlot={selectedSlot}
                          setSelectedSlot={(value: string) => {
                            setSelectedSlot(value)
                            setValue('start', moment(value).toISOString())
                          }}
                          slot={slot}
                          key={`suggestion_${index}`}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="emptySuggestions">
                      <img src="/schedule.svg" alt="Schedule" />
                      <h6> {t('noSuggestions')} </h6>
                    </div>
                  )}
                </div>
              </SuggesstionList>
            }
          </CreateSessionContent>
        </CreateSessionForm>
      </DialogContent>
      <DialogActions
        sx={{ display: 'grid', justifyContent: 'stretch', p: '16px 22px' }}
      >
        <Button
          disabled={loading || isLoading || updateLoading || !selectedSlot}
          type="submit"
          form="sessionForm"
          variant="contained"
          size="large"
        >
          {t(
            loading
              ? 'checkingForAvailabilities'
              : isLoading || updateLoading
                ? 'loading'
                : initialData?.id
                  ? 'save'
                  : 'book',
          )}
        </Button>
      </DialogActions>
    </CreateSessionWrapper>
  )
}
